// ============================================================
//  src/pages/rent-space/RentSpacePage.js
//
//  User-facing "Rent Space" — register a WhatsApp number under the
//  platform's own WABA and pay monthly via PocketFi, without needing
//  a Meta developer account. Same 4-step Meta registration flow as
//  PhoneRegistrationWizard, but the platform supplies the WhatsApp
//  token/WABA ID (never shown to the user), plus a payment step at
//  the end instead of a "copy your keys" success screen.
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import './RentSpacePage.css';
import {
  getRentSpaceInfo, getMyRentals,
  rentSpaceAddNumber, rentSpaceRequestCode, rentSpaceVerifyCode, rentSpaceComplete,
  rentSpaceRetryPayment, rentSpaceRenew, createBotFromRental,
} from '../../api/client';

// Below this many seconds remaining, a still-active rental starts showing
// an urgent "renew soon" warning instead of a plain countdown. 3 days.
const RENEWAL_WARNING_SECONDS = 3 * 24 * 60 * 60;
const BILLING_PERIOD_SECONDS  = 30 * 24 * 60 * 60;

/** "expired" | "3d 4h" | "12h 5m" | null (no countdown applies) */
function formatCountdown(secondsRemaining) {
  if (secondsRemaining == null) return null;
  if (secondsRemaining <= 0) return 'expired';
  const days  = Math.floor(secondsRemaining / 86400);
  const hours = Math.floor((secondsRemaining % 86400) / 3600);
  const mins  = Math.floor((secondsRemaining % 3600) / 60);
  if (days > 0)  return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

const naira = n => '₦' + Number(n || 0).toLocaleString();

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T'));
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const COUNTRY_CODES = [
  ['234', 'Nigeria', '🇳🇬'], ['1', 'US / Canada', '🇺🇸'], ['44', 'United Kingdom', '🇬🇧'],
  ['91', 'India', '🇮🇳'], ['27', 'South Africa', '🇿🇦'], ['254', 'Kenya', '🇰🇪'],
  ['233', 'Ghana', '🇬🇭'], ['256', 'Uganda', '🇺🇬'], ['255', 'Tanzania', '🇹🇿'],
];

const STATUS_META = {
  pending_payment: { label: 'Awaiting payment', cls: 'is-pending', icon: 'fa-clock' },
  active:          { label: 'Active',           cls: 'is-active',  icon: 'fa-circle-check' },
  paused:          { label: 'Paused',           cls: 'is-paused',  icon: 'fa-pause' },
  deregistered:    { label: 'Deregistered',     cls: 'is-dereg',   icon: 'fa-ban' },
};

function extractErrorMessage(err, fallback) {
  return err?.response?.data?.message ?? fallback;
}

function Banner({ tone = 'info', icon, title, children, action }) {
  const icons = { info: 'fa-circle-info', warning: 'fa-triangle-exclamation', danger: 'fa-circle-exclamation', success: 'fa-circle-check' };
  return (
    <div className={`rsp-banner ${tone}`} role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}>
      <i className={`fa-solid ${icon || icons[tone]} rsp-banner-ic`} />
      <div className="rsp-banner-body">
        {title && <strong>{title}</strong>}
        {children && <span>{children}</span>}
      </div>
      {action}
    </div>
  );
}

function Spinner({ light }) { return <span className={`rsp-spin ${light ? 'light' : ''}`} aria-hidden="true" />; }

// ── Page ─────────────────────────────────────────────────────────
export default function RentSpacePage() {
  const [loading, setLoading]   = useState(true);
  const [info, setInfo]         = useState(null);     // { monthlyPrice, available, maxPerUser, canRentMore, spacesUsed }
  const [rentals, setRentals]   = useState([]);        // every rental this customer holds
  const [showAddAnother, setShowAddAnother] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const [infoRes, rentalsRes] = await Promise.all([getRentSpaceInfo(), getMyRentals()]);
      if (infoRes.data?.success) setInfo(infoRes.data);
      if (rentalsRes.data?.success) {
        setRentals(rentalsRes.data.rentals ?? []);
        setShowAddAnother(false);
      }
    } catch {
      toast.error('Could not load Rent Space');
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  const refresh = () => load(true);

  let body;
  let headerAction = null;

  if (loading) {
    body = (
      <div className="rsp-skel">
        <div className="rsp-card"><span className="sk w40" /><span className="sk w70 h24" /><span className="sk w90" /><span className="sk w60" /></div>
        <div className="rsp-card"><span className="sk w30" /><span className="sk w80" /><span className="sk w50" /></div>
      </div>
    );
  } else if (!info?.available) {
    body = (
      <div className="rsp-card rsp-empty">
        <span className="rsp-empty-ic"><i className="fa-solid fa-tower-broadcast" /></span>
        <h3>Rent Space isn’t available yet</h3>
        <p>We’re still setting this up on our side. Check back soon — or connect your own WhatsApp number from the Dashboard in the meantime.</p>
      </div>
    );
  } else {
    const unfinished = rentals.find(r => r.rnStatus === 'pending_payment' && !r.rnPhoneNumberId);

    if (rentals.length === 0 && !showAddAnother) {
      body = <RentWizardView monthlyPrice={info.monthlyPrice} onRentalStarted={refresh} firstTime />;
    } else if (unfinished) {
      body = <RentWizardView monthlyPrice={info.monthlyPrice} resumeRnId={unfinished.rnId} resumeNumber={unfinished} onRentalStarted={refresh} />;
    } else if (showAddAnother) {
      body = <RentWizardView monthlyPrice={info.monthlyPrice} onRentalStarted={refresh} onCancel={() => setShowAddAnother(false)} />;
    } else {
      const max = Number(info.maxPerUser) || 0;
      const used = Number(info.spacesUsed ?? rentals.length);
      headerAction = (
        <button type="button" className="rsp-btn ghost" onClick={refresh} disabled={refreshing}>
          <i className={`fa-solid fa-rotate-right ${refreshing ? 'fa-spin' : ''}`} /> Refresh
        </button>
      );
      body = (
        <>
          <div className="rsp-usage">
            <div className="rsp-usage-txt">
              <span><b>{used}</b> of {max > 0 ? max : 'unlimited'} space{used === 1 ? '' : 's'} in use</span>
              <span className="rsp-usage-price">{naira(info.monthlyPrice)} / month each</span>
            </div>
            {max > 0 && <div className="rsp-meter"><span style={{ width: `${Math.min(100, (used / max) * 100)}%` }} /></div>}
          </div>

          <div className="rsp-grid">
            {rentals.map(r => <RentalStatusView key={r.rnId} rental={r} onRefresh={refresh} />)}

            {info.canRentMore ? (
              <button type="button" className="rsp-add-card" onClick={() => setShowAddAnother(true)}>
                <span className="rsp-add-ic"><i className="fa-solid fa-plus" /></span>
                <b>Rent another number</b>
                <em>{naira(info.monthlyPrice)}/month · run another bot on a different number</em>
              </button>
            ) : (
              <div className="rsp-add-card is-full">
                <span className="rsp-add-ic"><i className="fa-solid fa-lock" /></span>
                <b>Space limit reached</b>
                <em>You’re using all {max} rented space{max === 1 ? '' : 's'}. Contact support if you need more.</em>
              </div>
            )}
          </div>
        </>
      );
    }
  }

  return (
    <div className="rsp-page">
      <header className="rsp-head">
        <div>
          <h1>Rent Space</h1>
          <p>Get your own WhatsApp Business number live in minutes — we handle all the Meta setup for you.</p>
        </div>
        {headerAction}
      </header>
      {body}
    </div>
  );
}

const STATUS_EXPLANATIONS = {
  deregistered: 'This number has been deregistered and can no longer send or receive messages through this platform. Contact support if you believe this is a mistake.',
};

// ── Rental card — already renting (or mid-payment) ──────────────
function RentalStatusView({ rental, onRefresh }) {
  const meta = STATUS_META[rental.rnStatus] ?? STATUS_META.pending_payment;
  const isPendingUnregistered = rental.rnStatus === 'pending_payment' && !rental.rnPhoneNumberId;

  const [retrying, setRetrying] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [showCreateBot, setShowCreateBot] = useState(false);
  const [botName, setBotName] = useState('');
  const [creating, setCreating] = useState(false);

  if (isPendingUnregistered) {
    return <RentWizardView monthlyPrice={rental.rnMonthlyAmount} resumeRnId={rental.rnId} resumeNumber={rental} onRentalStarted={onRefresh} />;
  }

  const countdown = rental.rnStatus === 'active' ? formatCountdown(rental.secondsRemaining) : null;
  const isUrgent  = rental.isExpired || (rental.secondsRemaining != null && rental.secondsRemaining <= RENEWAL_WARNING_SECONDS);
  const periodPct = rental.secondsRemaining != null ? Math.max(0, Math.min(100, (rental.secondsRemaining / BILLING_PERIOD_SECONDS) * 100)) : null;
  const number    = `+${rental.rnCountryCode}${rental.rnPhoneNumber}`;

  async function handleRenew() {
    setRenewing(true);
    try {
      const res = await rentSpaceRenew(rental.rnId);
      if (res.data?.success) {
        window.location.href = res.data.paymentLink;
      } else {
        toast.error(res.data?.message ?? 'Could not start payment');
      }
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Network error'));
    }
    setRenewing(false);
  }

  async function handleRetryPayment() {
    setRetrying(true);
    try {
      const res = await rentSpaceRetryPayment(rental.rnId);
      if (res.data?.success) {
        window.location.href = res.data.paymentLink;
      } else {
        toast.error(res.data?.message ?? 'Could not start payment');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setRetrying(false);
  }

  async function handleCreateBot(e) {
    e.preventDefault();
    if (!botName.trim()) return toast.error('Bot name is required');
    setCreating(true);
    try {
      const res = await createBotFromRental(rental.rnId, botName.trim());
      if (res.data?.success) {
        toast.success('Bot created! Find it on your Dashboard.');
        onRefresh();
      } else {
        toast.error(res.data?.message ?? 'Could not create bot');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Network error');
    }
    setCreating(false);
  }

  const copy = (text, label) => navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {});

  return (
    <article className={`rsp-card rsp-rental ${meta.cls} ${isUrgent && countdown ? 'is-urgent' : ''}`}>
      <div className="rsp-rental-head">
        <span className="rsp-wa-ic"><i className="fa-brands fa-whatsapp" /></span>
        <div className="rsp-rental-id">
          <h3>{rental.rnDisplayName || 'Rented number'}</h3>
          <button type="button" className="rsp-number" onClick={() => copy(number, 'Number')} title="Copy number">
            {number} <i className="fa-regular fa-copy" />
          </button>
        </div>
        <span className={`rsp-status ${meta.cls}`}><i className={`fa-solid ${meta.icon}`} /> {meta.label}</span>
      </div>

      {countdown && (
        <div className={`rsp-period ${isUrgent ? 'is-urgent' : ''}`}>
          <div className="rsp-period-top">
            <span>{rental.isExpired ? 'Subscription expired' : 'Renews in'}</span>
            <b>{rental.isExpired ? 'Expired' : countdown}</b>
          </div>
          {periodPct !== null && <div className="rsp-meter"><span style={{ width: `${periodPct}%` }} /></div>}
        </div>
      )}

      <dl className="rsp-details">
        <div><dt>Monthly fee</dt><dd>{naira(rental.rnMonthlyAmount)}</dd></div>
        <div><dt>Next billing</dt><dd>{rental.rnNextBillingAt ? fmtDate(rental.rnNextBillingAt) : '—'}</dd></div>
        <div className="span-2">
          <dt>Phone number ID</dt>
          <dd>
            {rental.rnPhoneNumberId
              ? <button type="button" className="rsp-mono" onClick={() => copy(rental.rnPhoneNumberId, 'Phone number ID')} title="Copy">
                  {rental.rnPhoneNumberId} <i className="fa-regular fa-copy" />
                </button>
              : '—'}
          </dd>
        </div>
        <div className="span-2">
          <dt>Linked bot</dt>
          <dd>{rental.rnLinkedBotId ? <span className="rsp-linked"><i className="fa-solid fa-robot" /> Connected</span> : <span className="rsp-muted">Not connected yet</span>}</dd>
        </div>
      </dl>

      {/* ── State-specific messages & actions ── */}
      <div className="rsp-rental-actions">
        {rental.rnStatus === 'deregistered' && (
          <Banner tone="danger" title="Number deregistered">{STATUS_EXPLANATIONS.deregistered}</Banner>
        )}

        {countdown && rental.isExpired && rental.rnLinkedBotId && (
          <Banner tone="danger" title="Your bot has stopped responding">This space’s payment is overdue, so its bot is paused until you renew.</Banner>
        )}
        {countdown && !rental.isExpired && isUrgent && (
          <Banner tone="warning" title="Renew soon">Renew before the countdown ends so your bot keeps replying.</Banner>
        )}
        {countdown && (rental.isExpired || isUrgent) && (
          <button type="button" className="rsp-btn primary full" onClick={handleRenew} disabled={renewing}>
            {renewing ? <><Spinner /> Starting payment…</> : <>Renew now · {naira(rental.rnMonthlyAmount)} <i className="fa-solid fa-arrow-right" /></>}
          </button>
        )}
        {countdown && rental.rnStatus === 'active' && (
          <Link to="/wallet" className="rsp-wallet-link">
            <i className="fa-solid fa-wallet" /> Pay from your wallet, or turn on auto-renew so this never lapses <i className="fa-solid fa-arrow-right" />
          </Link>
        )}

        {rental.rnStatus === 'active' && rental.rnPhoneNumberId && !rental.rnLinkedBotId && !showCreateBot && (
          <>
            <Banner tone="success" title="Ready to use">Create a bot on this number — no token or setup needed, we handle that automatically.</Banner>
            <button type="button" className="rsp-btn dark full" onClick={() => setShowCreateBot(true)}>
              <i className="fa-solid fa-robot" /> Create a bot with this number
            </button>
          </>
        )}
        {rental.rnStatus === 'active' && rental.rnLinkedBotId && !countdown?.startsWith?.('expired') && !isUrgent && (
          <Banner tone="info">A bot is using this number. Manage it from your Dashboard.</Banner>
        )}
        {showCreateBot && (
          <form onSubmit={handleCreateBot} className="rsp-create-bot">
            <label className="rsp-label" htmlFor={`bot-${rental.rnId}`}>Bot name</label>
            <div className="rsp-inline">
              <input id={`bot-${rental.rnId}`} className="rsp-input" placeholder="e.g. Notifier Bot" value={botName}
                onChange={e => setBotName(e.target.value)} autoFocus />
              <button type="submit" className="rsp-btn primary" disabled={creating}>
                {creating ? <><Spinner /> Creating…</> : 'Create bot'}
              </button>
            </div>
            <button type="button" className="rsp-text-btn" onClick={() => setShowCreateBot(false)}>Cancel</button>
          </form>
        )}

        {rental.rnStatus === 'paused' && (
          <Banner tone="warning" title="Number paused">This number is currently paused. Contact support if you believe this is a mistake.</Banner>
        )}
        {rental.rnStatus === 'pending_payment' && rental.rnPhoneNumberId && (
          <>
            <Banner tone="warning" title="Payment not completed">
              Registration finished, but payment wasn’t completed. Pay below to activate this number — contact support if you were charged but it still shows pending.
            </Banner>
            <button type="button" className="rsp-btn primary full" onClick={handleRetryPayment} disabled={retrying}>
              {retrying ? <><Spinner /> Starting payment…</> : <>Complete payment · {naira(rental.rnMonthlyAmount)} <i className="fa-solid fa-arrow-right" /></>}
            </button>
          </>
        )}
      </div>
    </article>
  );
}

// ── Wizard — start (or resume) renting a number ─────────────────
const WIZ_STEPS = [
  { key: 'details', label: 'Number' },
  { key: 'code',    label: 'Verify' },
  { key: 'pin',     label: 'Secure & pay' },
];
const stepIndex = s => (s === 'details' ? 0 : s === 'code-choice' || s === 'code-entry' ? 1 : s === 'pin' ? 2 : -1);

function RentWizardView({ monthlyPrice, resumeRnId, resumeNumber, onRentalStarted, onCancel, firstTime }) {
  const [step, setStep] = useState(resumeRnId ? 'code-choice' : 'intro'); // intro | details | code-choice | code-entry | pin
  const [busy, setBusy] = useState(false);
  const [rnId, setRnId] = useState(resumeRnId || null);
  const [apiError, setApiError] = useState(null);

  const [form, setForm] = useState({
    cc: resumeNumber?.rnCountryCode || '234',
    phoneNumber: resumeNumber?.rnPhoneNumber || '',
    displayName: resumeNumber?.rnDisplayName || '',
  });
  const [codeMethod, setCodeMethod] = useState('SMS');
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const topRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code-entry') setTimeout(() => document.getElementById('rsp-code-digit-0')?.focus(), 50);
    if (step !== 'intro') topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [step]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (apiError) setApiError(null); };
  const fullNumber = form.phoneNumber ? `+${form.cc} ${form.phoneNumber}` : null;

  async function handleAddNumber(e) {
    e.preventDefault();
    setApiError(null);
    if (!form.phoneNumber.trim())  return toast.error('Phone number is required');
    if (!form.displayName.trim())  return toast.error('Display name is required');

    setBusy(true);
    try {
      const res = await rentSpaceAddNumber(form.cc, form.phoneNumber.trim(), form.displayName.trim());
      if (res.data?.success) {
        setRnId(res.data.rnId);
        setStep('code-choice');
      } else {
        setApiError(res.data?.message ?? 'Could not add this number');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  async function handleSendCode(method) {
    setCodeMethod(method);
    setApiError(null);
    setBusy(true);
    try {
      const res = await rentSpaceRequestCode(rnId, method);
      if (res.data?.success) {
        toast.success(`Code sent via ${method === 'VOICE' ? 'voice call' : 'SMS'}`);
        setCodeDigits(['', '', '', '', '', '']);
        setCooldown(60);
        setStep('code-entry');
      } else {
        setApiError(res.data?.message ?? 'Could not send the code');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  function handleDigitChange(i, raw) {
    const clean = raw.replace(/\D/g, '');
    if (clean.length > 1) {                         // paste / autofill
      const next = [...codeDigits];
      for (let k = 0; k < clean.length && i + k < 6; k++) next[i + k] = clean[k];
      setCodeDigits(next);
      const last = Math.min(i + clean.length, 5);
      document.getElementById(`rsp-code-digit-${last}`)?.focus();
      if (next.every(d => d !== '')) handleVerifyCode(next.join(''));
      return;
    }
    if (!/^\d?$/.test(clean)) return;
    const next = [...codeDigits];
    next[i] = clean;
    setCodeDigits(next);
    if (clean && i < 5) document.getElementById(`rsp-code-digit-${i + 1}`)?.focus();
    if (clean && i === 5 && next.every(d => d !== '')) handleVerifyCode(next.join(''));
  }

  function handleCodePaste(e) {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    handleDigitChange(0, text);
  }

  async function handleVerifyCode(codeOverride) {
    const code = codeOverride ?? codeDigits.join('');
    if (code.length !== 6) return toast.error('Enter all 6 digits');
    setApiError(null);
    setBusy(true);
    try {
      const res = await rentSpaceVerifyCode(rnId, code);
      if (res.data?.success) {
        toast.success('Number verified');
        setStep('pin');
      } else {
        setApiError(res.data?.message ?? 'That code didn\'t work');
        setCodeDigits(['', '', '', '', '', '']);
        document.getElementById('rsp-code-digit-0')?.focus();
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  async function handleComplete(e) {
    e.preventDefault();
    if (!/^\d{6}$/.test(pin)) return toast.error('PIN must be exactly 6 digits');
    if (pin !== pinConfirm)   return toast.error('PINs do not match');
    setApiError(null);
    setBusy(true);
    try {
      const res = await rentSpaceComplete(rnId, pin, pinConfirm);
      if (res.data?.success) {
        toast.success('Registered! Redirecting you to payment…');
        onRentalStarted();
        window.location.href = res.data.paymentLink;
      } else {
        setApiError(res.data?.message ?? 'Registration failed');
      }
    } catch (err) {
      setApiError(extractErrorMessage(err, 'Network error — could not reach the server'));
    }
    setBusy(false);
  }

  // ── Intro / landing ──
  if (step === 'intro') {
    return (
      <div className="rsp-intro">
        <section className="rsp-hero">
          <div className="rsp-hero-glow" aria-hidden="true" />
          <div className="rsp-hero-copy">
            <span className="rsp-eyebrow"><i className="fa-brands fa-whatsapp" /> WhatsApp Business number</span>
            <h2>{firstTime ? 'Your bot’s own WhatsApp number — without the Meta paperwork.' : 'Rent another WhatsApp number'}</h2>
            <p>Register your number under our verified WhatsApp Business Account. No developer account, no App Setup screens — just verify your number and pay monthly.</p>
            <ul className="rsp-hero-points">
              <li><i className="fa-solid fa-check" /> No Meta developer account needed</li>
              <li><i className="fa-solid fa-check" /> Verify with a code by SMS or voice call</li>
              <li><i className="fa-solid fa-check" /> Create a bot on it instantly — no tokens</li>
            </ul>
          </div>

          <div className="rsp-price-card">
            <span className="rsp-price-label">Simple monthly price</span>
            <div className="rsp-price">{naira(monthlyPrice)}<span>/ month</span></div>
            <ul>
              <li><i className="fa-solid fa-circle-check" /> 1 WhatsApp number</li>
              <li><i className="fa-solid fa-circle-check" /> Official Cloud API connection</li>
              <li><i className="fa-solid fa-circle-check" /> Connect it to any of your bots</li>
              <li><i className="fa-solid fa-circle-check" /> Secure payment via PocketFi</li>
            </ul>
            <button type="button" className="rsp-btn primary full lg" onClick={() => setStep('details')}>
              Get started <i className="fa-solid fa-arrow-right" />
            </button>
            {onCancel && (
              <button type="button" className="rsp-text-btn center" onClick={onCancel}>
                <i className="fa-solid fa-arrow-left" /> Back to my spaces
              </button>
            )}
          </div>
        </section>

        <section className="rsp-how">
          <h3>How it works</h3>
          <ol>
            <li><span>1</span><b>Add your number</b><p>Enter the number and the name customers will see.</p></li>
            <li><span>2</span><b>Verify it</b><p>WhatsApp sends a 6-digit code by SMS or voice call.</p></li>
            <li><span>3</span><b>Set a PIN</b><p>A 6-digit security PIN — we store it safely for you.</p></li>
            <li><span>4</span><b>Pay & go live</b><p>Pay monthly, then create a bot on the number.</p></li>
          </ol>
        </section>
      </div>
    );
  }

  const idx = stepIndex(step);

  // ── Wizard steps ──
  return (
    <div className="rsp-wizard" ref={topRef}>
      <div className="rsp-card rsp-wiz-main">
        <div className="rsp-wiz-top">
          {onCancel ? (
            <button type="button" className="rsp-text-btn" onClick={onCancel}><i className="fa-solid fa-arrow-left" /> My spaces</button>
          ) : step === 'details' ? (
            <button type="button" className="rsp-text-btn" onClick={() => setStep('intro')}><i className="fa-solid fa-arrow-left" /> Back</button>
          ) : <span />}
          <span className="rsp-wiz-count">Step {idx + 1} of 3</span>
        </div>

        <ol className="rsp-steps" aria-label="Progress">
          {WIZ_STEPS.map((s, i) => (
            <li key={s.key} className={i < idx ? 'done' : i === idx ? 'current' : ''}>
              <span className="rsp-step-dot">{i < idx ? <i className="fa-solid fa-check" /> : i + 1}</span>
              <span className="rsp-step-label">{s.label}</span>
            </li>
          ))}
        </ol>

        {apiError && (
          <Banner tone="danger" action={
            <button type="button" className="rsp-icon-btn" onClick={() => setApiError(null)} aria-label="Dismiss"><i className="fa-solid fa-xmark" /></button>
          }>{apiError}</Banner>
        )}

        {step === 'details' && (
          <form onSubmit={handleAddNumber} className="rsp-form">
            <div className="rsp-step-head">
              <h2>Which number do you want to use?</h2>
              <p>Use a number that isn’t currently active on the WhatsApp or WhatsApp Business app.</p>
            </div>

            <div className="rsp-field">
              <label className="rsp-label" htmlFor="rsp-phone">Phone number</label>
              <div className="rsp-phone">
                <label className="rsp-cc">
                  <span className="rsp-cc-flag">{COUNTRY_CODES.find(c => c[0] === form.cc)?.[2]}</span>
                  <span className="rsp-cc-code">+{form.cc}</span>
                  <i className="fa-solid fa-chevron-down" />
                  <select value={form.cc} onChange={e => set('cc', e.target.value)} aria-label="Country code">
                    {COUNTRY_CODES.map(([code, label]) => <option key={code} value={code}>{label} (+{code})</option>)}
                  </select>
                </label>
                <input id="rsp-phone" className="rsp-phone-input" inputMode="numeric" autoComplete="tel-national" placeholder="801 234 5678"
                  value={form.phoneNumber} onChange={e => set('phoneNumber', e.target.value.replace(/\D/g, '').replace(/^0+/, ''))} autoFocus />
              </div>
              <div className="rsp-hint">Without the country code or the leading 0.</div>
            </div>

            <div className="rsp-field">
              <label className="rsp-label" htmlFor="rsp-name">Display name</label>
              <input id="rsp-name" className="rsp-input" placeholder="e.g. DataHub NG" maxLength={60} value={form.displayName}
                onChange={e => set('displayName', e.target.value)} />
              <div className="rsp-hint">This is the business name customers see in WhatsApp.</div>
            </div>

            <button type="submit" className="rsp-btn primary full lg" disabled={busy}>
              {busy ? <><Spinner /> Adding number…</> : <>Continue <i className="fa-solid fa-arrow-right" /></>}
            </button>
          </form>
        )}

        {step === 'code-choice' && (
          <div className="rsp-form">
            <div className="rsp-step-head">
              <h2>How should we send your code?</h2>
              <p>WhatsApp will send a 6-digit verification code to <b>{fullNumber || 'your number'}</b>.</p>
            </div>
            <div className="rsp-choices">
              <button type="button" className="rsp-choice" disabled={busy} onClick={() => handleSendCode('SMS')}>
                <span className="rsp-choice-ic"><i className="fa-solid fa-comment-sms" /></span>
                <span className="rsp-choice-txt"><b>Text message</b><em>Fastest — arrives in seconds</em></span>
                {busy && codeMethod === 'SMS' ? <Spinner /> : <i className="fa-solid fa-chevron-right rsp-choice-go" />}
              </button>
              <button type="button" className="rsp-choice" disabled={busy} onClick={() => handleSendCode('VOICE')}>
                <span className="rsp-choice-ic"><i className="fa-solid fa-phone-volume" /></span>
                <span className="rsp-choice-txt"><b>Voice call</b><em>We’ll call and read the code out</em></span>
                {busy && codeMethod === 'VOICE' ? <Spinner /> : <i className="fa-solid fa-chevron-right rsp-choice-go" />}
              </button>
            </div>
          </div>
        )}

        {step === 'code-entry' && (
          <div className="rsp-form">
            <div className="rsp-step-head">
              <h2>Enter the 6-digit code</h2>
              <p>Sent by {codeMethod === 'VOICE' ? 'voice call' : 'SMS'} to <b>{fullNumber || 'your number'}</b>.</p>
            </div>
            <div className="rsp-otp" onPaste={handleCodePaste}>
              {codeDigits.map((d, i) => (
                <input key={i} id={`rsp-code-digit-${i}`} className={`rsp-otp-digit ${d ? 'filled' : ''}`}
                  inputMode="numeric" autoComplete={i === 0 ? 'one-time-code' : 'off'} maxLength={i === 0 ? 6 : 1}
                  aria-label={`Digit ${i + 1}`} value={d} disabled={busy}
                  onChange={e => handleDigitChange(i, e.target.value)}
                  onFocus={e => e.target.select()}
                  onKeyDown={e => { if (e.key === 'Backspace' && !d && i > 0) document.getElementById(`rsp-code-digit-${i - 1}`)?.focus(); }} />
              ))}
            </div>
            <button type="button" className="rsp-btn primary full lg" disabled={busy || codeDigits.join('').length !== 6} onClick={() => handleVerifyCode()}>
              {busy ? <><Spinner /> Verifying…</> : <>Verify number <i className="fa-solid fa-arrow-right" /></>}
            </button>
            <div className="rsp-resend">
              <span>Didn’t get it?</span>
              {cooldown > 0
                ? <span className="rsp-muted">Resend in {cooldown}s</span>
                : <button type="button" className="rsp-text-btn brand" disabled={busy} onClick={() => handleSendCode(codeMethod)}>Resend code</button>}
              <span className="rsp-dot" />
              <button type="button" className="rsp-text-btn" disabled={busy} onClick={() => setStep('code-choice')}>
                Try {codeMethod === 'VOICE' ? 'SMS' : 'a voice call'}
              </button>
            </div>
          </div>
        )}

        {step === 'pin' && (
          <form onSubmit={handleComplete} className="rsp-form">
            <div className="rsp-step-head">
              <h2>Secure your number</h2>
              <p>Set a 6-digit PIN for this number. You won’t need to remember it — we store it securely for you.</p>
            </div>
            <div className="rsp-row-2">
              <div className="rsp-field">
                <label className="rsp-label" htmlFor="rsp-pin">6-digit PIN</label>
                <div className="rsp-pin-wrap">
                  <input id="rsp-pin" className="rsp-input rsp-pin" type={showPin ? 'text' : 'password'} inputMode="numeric" maxLength={6} placeholder="••••••"
                    autoComplete="new-password" value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} autoFocus />
                  <button type="button" className="rsp-icon-btn" onClick={() => setShowPin(v => !v)} aria-label={showPin ? 'Hide PIN' : 'Show PIN'}>
                    <i className={`fa-solid ${showPin ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>
              <div className="rsp-field">
                <label className="rsp-label" htmlFor="rsp-pin2">Confirm PIN</label>
                <input id="rsp-pin2" className={`rsp-input rsp-pin ${pinConfirm.length === 6 ? (pin === pinConfirm ? 'is-ok' : 'is-bad') : ''}`}
                  type={showPin ? 'text' : 'password'} inputMode="numeric" maxLength={6} placeholder="••••••"
                  autoComplete="new-password" value={pinConfirm} onChange={e => setPinConfirm(e.target.value.replace(/\D/g, ''))} />
                {pinConfirm.length === 6 && (
                  <div className={`rsp-hint ${pin === pinConfirm ? 'ok' : 'bad'}`}>
                    <i className={`fa-solid ${pin === pinConfirm ? 'fa-check' : 'fa-xmark'}`} /> {pin === pinConfirm ? 'PINs match' : 'PINs don’t match'}
                  </div>
                )}
              </div>
            </div>

            <div className="rsp-checkout">
              <div><span>Rented number</span><b>{fullNumber || '—'}</b></div>
              <div><span>Monthly fee</span><b>{naira(monthlyPrice)}</b></div>
              <div className="rsp-checkout-total"><span>Due today</span><b>{naira(monthlyPrice)}</b></div>
            </div>

            <button type="submit" className="rsp-btn primary full lg" disabled={busy}>
              {busy ? <><Spinner /> Completing…</> : <><i className="fa-solid fa-lock" /> Complete & pay {naira(monthlyPrice)}</>}
            </button>
            <p className="rsp-secure-note"><i className="fa-solid fa-shield-halved" /> You’ll be redirected to PocketFi to pay securely.</p>
          </form>
        )}
      </div>

      <aside className="rsp-card rsp-wiz-side">
        <h4>Your rental</h4>
        <div className="rsp-sum-num">
          <span className="rsp-wa-ic sm"><i className="fa-brands fa-whatsapp" /></span>
          <div>
            <b>{form.displayName || 'Display name'}</b>
            <span>{fullNumber || '+234 ···'}</span>
          </div>
        </div>
        <div className="rsp-sum-row"><span>Price</span><b>{naira(monthlyPrice)} / month</b></div>
        <div className="rsp-sum-row"><span>Billing</span><b>Monthly via PocketFi</b></div>
        <div className="rsp-sum-tip">
          <i className="fa-solid fa-lightbulb" />
          <span>After payment, open this page again and tap <b>Create a bot</b> — the number is connected automatically.</span>
        </div>
      </aside>
    </div>
  );
}
